import { inject, Injectable, signal } from '@angular/core';

import {
  IAllFavoritesTeams,
  IFavoritesTeams,
} from '@/app/models/favorite/team-favorites.model';
import { EGoogleTagAnalyticsKeys } from '@/app/pages/sports/football/enums';
import { AuthService } from '@/app/services/auth/auth.service';
import { catchError, EMPTY, expand, map, Observable, of, reduce } from 'rxjs';
import { FsApiService } from '../auth/fs-api.service';
import { GoogleTagAnalyticsService } from '../google-tag';
import { LocalStorageService } from '../localstorage.service';
import { TranslationService } from '../translation.service';
import { FavoritesService } from './favorites.service';
import { pageSize } from '@/app/utils/constants';

const ID_LIST_TEAM_FAVORITES = 'idListTeamFavorites';

export interface IBodyFTeam {
  countryEn: string;
  countryEs: string;
  idTeam: string;
  logo: string;
  logoCountry: string;
  nameEn: string;
  nameEs: string;
  sport: string;

  fsIdTeam: string;
}

@Injectable({
  providedIn: 'root',
})
export class NewTeamFavoritesService {
  private fsApi = inject(FsApiService);
  private translationService = inject(TranslationService);
  private googleTagAnalyticsService = inject(GoogleTagAnalyticsService);
  private lsService = inject(LocalStorageService);

  favoritesService = inject(FavoritesService);
  authService = inject(AuthService);

  isLoadingTeamsFavorites = signal<boolean>(false);
  isErrorTeamsFavorites = signal<boolean>(false);
  teamsFavorites = signal<IAllFavoritesTeams | null>(null);

  getAllFavoritesTeams() {
    this.isLoadingTeamsFavorites.set(true);
    this.isErrorTeamsFavorites.set(false);

    const pageSize = 10;
    let currentPage = 1;

    this.fsApi
      .get<IAllFavoritesTeams>(`/teams/favorites`, {
        params: { page: currentPage, size: pageSize },
      })
      .pipe(
        // Repite llamadas mientras haya más páginas
        expand((data) => {
          const res = data as IAllFavoritesTeams; // Forzamos el tipo
          if (!res || res.Page >= res.NumPages) {
            return EMPTY;
          }
          currentPage++;
          return this.fsApi.get<IAllFavoritesTeams>(`/teams/favorites`, {
            params: { page: currentPage, size: pageSize },
          }) as Observable<IAllFavoritesTeams>;
        }),
        // Acumula todos los resultados en un solo array
        reduce((acc: IFavoritesTeams[], data: IAllFavoritesTeams) => {
          return [...acc, ...data.Teams];
        }, []),
        // Manejo de errores
        catchError((err) => {
          console.error(err);
          this.isErrorTeamsFavorites.set(true);
          this.isLoadingTeamsFavorites.set(false);
          return of([]);
        }),
        // Transformar datos
        map((allTeams) => {
          return allTeams.map((team) => {
            this.favoritesService.checkFavoriteTeam(team.Id, team.IsFavorite);
            return {
              ...team,
              Link: this.translationService.getTraslateUrlTeamDetails(
                team.NameTranslation,
                team.Name,
                team.Id
              ),
            };
          });
        })
      )
      .subscribe((teams) => {
        this.teamsFavorites.set({
          Teams: teams,
          EndIndex: teams.length - 1,
          NumPages: 1,
          Page: 1,
          PageSize: teams.length,
          StartIndex: 0,
          Total: teams.length,
        });
        this.isLoadingTeamsFavorites.set(false);
      });
  }

  getFavoritesTeams(page?: number) {
    this.isLoadingTeamsFavorites.set(true);
    this.isErrorTeamsFavorites.set(false);

    this.fsApi
      .get<IAllFavoritesTeams>(`/teams/favorites`, {
        params: { page: page ?? 1, size: pageSize },
      })
      .pipe(
        // Manejo de errores
        catchError((err) => {
          this.isErrorTeamsFavorites.set(true);
          this.isLoadingTeamsFavorites.set(false);
          return of([]);
        }),
        // Transformar datos
        map((allTeams) => {
          const favTeams = allTeams as IAllFavoritesTeams;

          return {
            ...favTeams,
            Teams: favTeams.Teams.map((team) => {
              this.favoritesService.checkFavoriteTeam(team.Id, team.IsFavorite);
              return {
                ...team,
                Link: this.translationService.getTraslateUrlTeamDetails(
                  team.NameTranslation,
                  team.Name,
                  team.Id
                ),
              };
            }),
          };
        })
      )
      .subscribe((teams) => {
        if (this.teamsFavorites() && this.teamsFavorites()!.Teams.length > 0) {
          this.teamsFavorites.set({
            ...teams,
            Teams: [...this.teamsFavorites()?.Teams!, ...teams.Teams],
          });
        } else {
          this.teamsFavorites.set({
            ...teams,
          });
        }

        this.isLoadingTeamsFavorites.set(false);
      });
  }

  setTeamFavorites(body: IFavoritesTeams) {
    if (!this.favoritesService.validAuthFavorites()) return;

    this.favoritesService.postFavoritesTeam(body.Id);

    this.teamsFavorites.update((prev) => ({
      EndIndex: prev?.EndIndex ?? 0,
      NumPages: prev?.NumPages ?? 0,
      Page: prev?.Page ?? 0,
      PageSize: prev?.PageSize ?? 0,
      StartIndex: prev?.StartIndex ?? 0,
      Total: prev?.Total ?? 0,
      Teams: [body, ...(prev?.Teams ?? [])],
    }));

    // google analytics
    this.googleTagAnalyticsService.event(
      EGoogleTagAnalyticsKeys.ADD_TEAM_FAVORITE,
      {
        ...body,
        teamsId: body.Id,
        isFavorite: true,
      }
    );
  }

  deleteTeamFavorites(teamId: string) {
    if (!this.favoritesService.validAuthFavorites()) return;

    this.favoritesService.deleteFavoritesTeam(teamId);

    this.teamsFavorites.update((prev) => ({
      EndIndex: prev?.EndIndex ?? 0,
      NumPages: prev?.NumPages ?? 0,
      Page: prev?.Page ?? 0,
      PageSize: prev?.PageSize ?? 0,
      StartIndex: prev?.StartIndex ?? 0,
      Total: prev?.Total ?? 0,
      Teams: prev?.Teams.filter((team) => team.Id !== teamId) ?? [],
    }));

    // google analytics
    this.googleTagAnalyticsService.event(
      EGoogleTagAnalyticsKeys.REMOVE_TEAM_FAVORITE,
      {
        idTeams: teamId,
        isFavorite: false,
      }
    );
  }

  addTeamServiceTest(id: string) {
    this.favoritesService.idsTeamFavorites.update((prev) => [...prev, id]);
    localStorage.setItem(
      ID_LIST_TEAM_FAVORITES,
      JSON.stringify(this.favoritesService.idsTeamFavorites())
    );
  }
  deleteTeamServiceTest(id: string) {
    this.favoritesService.idsTeamFavorites.update((prev) =>
      prev.filter((teamId) => teamId !== id)
    );
    localStorage.setItem(
      ID_LIST_TEAM_FAVORITES,
      JSON.stringify(this.favoritesService.idsTeamFavorites())
    );
  }

  isFavoriteTeam(id: string) {
    return this.favoritesService.idsTeamFavorites().includes(id);
  }
}
